/*
  HODL Vault Hero V4 – Taktgeber und Bedienung. Ersetzt die Claude-Design-Laufzeit (DCLogic) des Prototyps.
  Zeitachse, Phasen, Entschlüsselung, Schweizer Zeile und Beschriftungen wie in «HODL Vault Hero v4.dc.html».
  04.11.2026 (Marco): «DEINE KEYS.» entschlüsselt sich synchron mit dem Öffnen des Tresors (Verriegelung dreht
  bis Tür schwingt auf), danach Goldglanz alle 11–15 s; kein Knopf «Überspringen» mehr; Tempo im Abschnitt.
  04.11.2026 (Marco), lebendiger Showroom: Im Endbild beginnen die Produkte nach 0,8 s kaum merklich zu schweben,
  ein Goldglanz gleitet einmal über alle drei. Danach tauscht jeder Sockel einmal sein Produkt (Mitte, links, rechts;
  erster Wechsel nach 11 s, dann alle 12 s, einstellbar), als Beamen mit Goldstaub. Kein Wechsel bei Hover, Fokus,
  nach einem Tipp in den Hero, wenn die Bühne nicht zu 60 % sichtbar ist, bei reduzierter Bewegung oder Datensparmodus.
  Bild, Name und Link wechseln gemeinsam: Die Beschriftung tauscht erst, wenn sie unsichtbar ist.

  Für den Shop ergänzt:
  - Headline, Knöpfe und Vertrauenszeile stehen sofort im HTML. Three.js und die Szene laden erst,
    wenn die Seite steht und der Browser Luft hat (requestIdleCallback).
  - Die Schleife läuft nur, solange der Hero sichtbar ist; nach dem Ende mit 30 Bildern pro Sekunde
    (Handy 24), bei reduzierter Bewegung nur, wenn sich etwas ändert. Shader kompilieren vorab asynchron.
  - Die Pixeldichte sinkt stufenweise, wenn ein Gerät die Bilder nicht schafft.
  - Theme-Editor: Beim Neuladen des Abschnitts räumt disconnectedCallback Schleife, Beobachter,
    Listener und WebGL-Kontext auf; die Module lädt der Browser nur einmal.
*/
(() => {
  if (customElements.get('hodl-vault-hero')) return;

  const END = 9.5;
  const PH = [['IDLE', 0], ['AUTHENTICATING', 1.15], ['UNLOCKING', 3.0], ['OPENING', 4.1], ['ENTERING', 5.3], ['REVEALING', 7.2], ['EXPLORE', 9.5]];
  const clamp = (x, a, b) => Math.min(b, Math.max(a, x));
  const seg = (t, a, b) => clamp((t - a) / (b - a), 0, 1);
  const eo3 = (x) => 1 - Math.pow(1 - x, 3);
  const sine = (x) => 0.5 - 0.5 * Math.cos(Math.PI * x);
  const HEX = '0123456789ABCDEF';
  // Dauer eines Produktwechsels in Sekunden (Beamen mit Goldstaub): Abbau, Lichtstrahl, Aufbau, Goldglanz
  const WECHSEL_S = 3.0;
  // Politur beim Eintreffen im Showroom: Goldglanz nacheinander über links, Mitte, rechts (Start in s, Dauer 1,2 s)
  const POLITUR = [[2, 1.4], [0, 1.85], [1, 2.3]];
  // Entschlüsselung in Szenenzeit: Start mit der drehenden Verriegelung, letzter Buchstabe beim Aufschwingen der Tür
  const ENT0 = 3.05;
  const ENT1 = 4.5;

  let module = null;
  const ladeModule = (threeUrl, sceneUrl) => {
    if (!module) module = Promise.all([import(threeUrl), import(sceneUrl)]);
    return module;
  };

  const webgl = () => {
    try {
      const c = document.createElement('canvas');
      const gl = c.getContext('webgl2') || c.getContext('webgl');
      if (!gl) return false;
      const ext = gl.getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext();
      return true;
    } catch (e) {
      return false;
    }
  };

  const spaeter = () => new Promise((res) => {
    const leer = () => ('requestIdleCallback' in window ? requestIdleCallback(() => res(), { timeout: 1200 }) : setTimeout(res, 300));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', leer, { once: true });
    else leer();
  });

  class HodlVaultHero extends HTMLElement {
    connectedCallback() {
      if (this.alive) return;
      this.alive = true;
      // Wieder eingesetzt (Theme-Editor): Zustand wie beim ersten Laden
      this.classList.remove('is-ready', 'is-failed');
      this.classList.add('is-loading');
      this.t = 0;
      this.clock = 0;
      this.last = performance.now();
      this.ptr = { x: 0, y: 0, sx: 0, sy: 0 };
      this.hot = -1;
      this.pickHot = -1;
      this.ready = false;
      this.failed = false;
      this.paused = false;
      this.onScreen = true;
      this.raf = 0;
      this.wachBis = 0;
      this.stacked = false;
      this.glanzLaeuft = false;
      this.phase = 'IDLE';
      // data-url-0 ergibt kein dataset-Feld (Bindestrich vor Ziffer), darum direkt lesen
      this.urls = [0, 1, 2].map((i) => this.getAttribute('data-url-' + i) || '');
      this.tempo = clamp(Number(this.dataset.tempo) || 1, 0.1, 4);
      this.autoplay = this.dataset.autoplay !== 'false';
      this.callouts = this.dataset.callouts !== 'false';

      this.mqRuhig = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mqSchmal = window.matchMedia('(max-width: 759.98px)');
      this.reduced = this.mqRuhig.matches;
      this.narrow = this.mqSchmal.matches;
      this.touch = window.matchMedia('(hover: none)').matches;

      this.sammle();
      // Lebendiger Showroom: Einstellungen, Zusatzprodukte (JSON aus dem Abschnitt) und Zustand
      this.schweben = this.dataset.schweben !== 'false';
      this.wAn = this.dataset.wechsel !== 'false';
      this.wStart = clamp(Number(this.dataset.wechselStart) || 11, 5, 60);
      this.wAbstand = clamp(Number(this.dataset.wechselAbstand) || 12, 8, 60);
      this.wHandy = this.dataset.wechselHandy || 'alle';
      this.pool = [];
      try {
        const j = this.q('pool');
        const roh = j ? JSON.parse(j.textContent || '[]') : [];
        this.pool = (Array.isArray(roh) ? roh : []).filter((d) => d && d.bild && d.url && d.titel && [0, 1, 2].includes(d.platz));
      } catch (e) {
        this.pool = [];
      }
      this.plan = [];
      this.wFertig = 0;
      this.wNaechst = 1e9;
      this.wStopp = false;
      this.wechsel = null;
      this.showT = 0;
      this.labelK = [1, 1, 1];
      this.fokus = false;
      this.buehneSichtbar = true;
      this.fx = { idle: 0, zeit: 0, k: 0, glanz: [-1, -1, -1] };
      this.neuBis = [0, 0, 0];
      // Ausgangsbeschriftung merken; beim Wiedereinsetzen (Theme-Editor) zurück, weil die Szene wieder mit ihr beginnt
      if (!this.startLabels) this.startLabels = [0, 1, 2].map((i) => this.labelVon(i));
      else this.startLabels.forEach((d, i) => { if (d) { this.beschrifte(i, d); this.navLabel(i); this.goldLabel(i, false); } });
      this.binde();
      this.onResize();
      this.t = this.reduced || !this.autoplay ? END : 0;
      this.decrypt(this.t);
      this.boot();
      this.vault = {
        seek: (s, pause = true) => { this.t = s; this.paused = pause; this.wecke(800); },
        play: () => { this.paused = false; this.wecke(); },
        replay: () => this.replay(true),
        time: () => this.t,
        ready: () => this.ready,
        failed: () => this.failed,
        dpr: () => this.dpr,
        // Prüfhilfen für den Showroom: nur lesen; wechselJetzt und planNeu wirken nur auf ausdrücklichen Aufruf
        showroom: () => ({
          zeit: +this.showT.toFixed(2),
          schweben: +this.fx.idle.toFixed(3),
          plan: this.plan.map((s) => ({ titel: this.pool[s.pool].titel, platz: this.pool[s.pool].platz, bereit: s.id != null, fehler: !!s.fehler })),
          fertig: this.wFertig,
          naechst: +this.wNaechst.toFixed(2),
          wechsel: this.wechsel ? { platz: this.wechsel.slot, k: +this.wechsel.k.toFixed(3) } : null,
          stopp: this.wStopp,
          links: this.urls.slice(),
          beschriftung: this.labelK.slice(),
        }),
        wechselJetzt: () => { this.wNaechst = this.showT; this.wecke(); },
        wechselHalt: (k) => { if (this.wechsel) { this.wechsel.halt = true; this.wechsel.k = clamp(k, 0, 1); this.wecke(400); } },
        wechselWeiter: () => { if (this.wechsel) { this.wechsel.halt = false; this.wecke(); } },
        probe: () => (this.scene && this.scene.probe ? this.scene.probe() : null),
        planNeu: (start) => {
          if (this.wechsel) return;
          this.plan = this.planen(start);
          this.wFertig = 0;
          this.wVorbereitet = false;
        },
      };
    }

    disconnectedCallback() {
      this.alive = false;
      cancelAnimationFrame(this.raf);
      this.raf = 0;
      clearTimeout(this.glanzT);
      this.ab.forEach((f) => f());
      this.ab = [];
      this.wechsel = null;
      if (this.scene) {
        this.scene.dispose();
        this.scene = null;
        // Der WebGL-Kontext dieser Fläche ist nach dispose verloren (forceContextLoss). Wird dasselbe Element wieder
        // eingesetzt (Theme-Editor: Abschnitt verschoben), braucht es eine frische Fläche, sonst bliebe nur das Ersatzbild.
        const cv = this.q('gl');
        if (cv && cv.parentNode) cv.replaceWith(cv.cloneNode(false));
      }
    }

    sammle() {
      this.E = {};
      this.querySelectorAll('[data-k]').forEach((n) => {
        const k = n.getAttribute('data-k');
        (this.E[k] = this.E[k] || []).push(n);
      });
      this.chars = Array.from(this.querySelectorAll('.hvh__c'));
    }

    q(k) {
      const a = this.E[k];
      return a ? a[0] : null;
    }

    on(ziel, typ, f, opt) {
      ziel.addEventListener(typ, f, opt);
      this.ab.push(() => ziel.removeEventListener(typ, f, opt));
    }

    binde() {
      this.ab = [];
      this.onResize = this.onResize.bind(this);
      this.tick = this.tick.bind(this);
      this.on(window, 'resize', this.onResize, { passive: true });
      this.on(window, 'pointermove', (e) => this.onPtr(e), { passive: true });
      this.on(document, 'visibilitychange', () => this.wecke());
      this.on(this.mqSchmal, 'change', () => { this.narrow = this.mqSchmal.matches; this.onResize(); this.wecke(400); });
      this.on(this.mqRuhig, 'change', () => { this.reduced = this.mqRuhig.matches; if (this.reduced && this.t < END) this.t = END; if (this.reduced) this.wStopp = true; this.wecke(); });
      // Tippen oder Klicken im Hero pausiert die Wechsel 6 s. Marco 04.11. (Handy): Jedes Scrollen beginnt mit einer
      // Berührung; früher stoppte schon sie die Wechsel für den ganzen Seitenaufruf, am Handy sah man darum keinen.
      // Fokus irgendwo im Hero (Tastatur) hält Wechsel an, solange er dort bleibt.
      this.on(this, 'pointerdown', () => { this.wNaechst = Math.max(this.wNaechst, this.showT + 6); }, { passive: true });
      this.on(this, 'focusin', () => { this.fokus = true; });
      this.on(this, 'focusout', (e) => { this.fokus = !!(e.relatedTarget && this.contains(e.relatedTarget)); });

      if ('ResizeObserver' in window) {
        const ro = new ResizeObserver(() => { this.onResize(); this.wecke(400); });
        ro.observe(this);
        const st = this.q('stage');
        if (st) ro.observe(st);
        this.ab.push(() => ro.disconnect());
      }
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((es) => {
          this.onScreen = es[es.length - 1].isIntersecting;
          this.wecke();
        });
        io.observe(this);
        this.ab.push(() => io.disconnect());
        // Wechsel nur, wenn die Bühne mindestens zu 60 % im Bild ist; halb verdeckt sähe ihn niemand richtig
        const bu = this.q('stage');
        if (bu) {
          const io2 = new IntersectionObserver((es) => {
            const e = es[es.length - 1];
            this.buehneSichtbar = e.isIntersecting && e.intersectionRatio >= 0.6;
          }, { threshold: [0, 0.6, 1] });
          io2.observe(bu);
          this.ab.push(() => io2.disconnect());
        }
      }

      (this.E.co || []).forEach((el) => {
        const i = Number(el.getAttribute('data-i'));
        const an = () => this.setHot(i);
        const aus = () => this.setHot(-1);
        this.on(el, 'mouseenter', an);
        this.on(el, 'focus', an);
        this.on(el, 'mouseleave', aus);
        this.on(el, 'blur', aus);
      });

      const cv = this.q('gl');
      if (cv) {
        const punkt = (e) => {
          const r = cv.getBoundingClientRect();
          return this.scene.pick(e.clientX - r.left, e.clientY - r.top);
        };
        this.on(cv, 'pointermove', (e) => {
          if (!this.scene || this.failed) return;
          const i = punkt(e);
          if (i !== this.pickHot) {
            this.pickHot = i;
            cv.style.cursor = i >= 0 ? 'pointer' : '';
            this.setHot(i);
          }
        });
        this.on(cv, 'pointerleave', () => {
          if (this.pickHot !== -1) {
            this.pickHot = -1;
            cv.style.cursor = '';
            this.setHot(-1);
          }
        });
        this.on(cv, 'click', (e) => {
          if (!this.scene) return;
          const i = punkt(e);
          // Während ein Platz wechselt, führt ein Klick dort nirgends hin (Bild und Ziel wären nicht eindeutig)
          if (this.wechsel && this.wechsel.slot === i) return;
          if (i >= 0 && this.urls[i]) window.location.href = this.urls[i];
        });
      }
    }

    setHot(i) {
      if (this.hot === i) return;
      this.hot = i;
      (this.E.co || []).forEach((el) => el.classList.toggle('is-hot', Number(el.getAttribute('data-i')) === i));
      this.wecke(900);
    }

    code() {
      const roh = String(this.dataset.code || '030109').replace(/\D/g, '');
      return (roh + '000000').slice(0, 6).split('').map(Number);
    }

    async boot() {
      const fail = (e) => {
        if (!this.alive) return;
        if (e) console.warn('[HODL Vault] Kein WebGL – statisches Ersatzbild', e && (e.message || e));
        this.failed = true;
        this.ready = true;
        this.t = END;
        this.classList.remove('is-loading');
        this.classList.add('is-failed');
        this.decrypt(END);
        this.glanzPlanen(1400);
        this.wecke();
      };
      if (this.narrow && this.dataset.mobil === 'bild') return fail();
      if (!webgl()) return fail('no webgl');
      await spaeter();
      if (!this.alive) return;
      try {
        const [THREE, mod] = await Promise.race([
          ladeModule(this.dataset.three, this.dataset.scene),
          new Promise((_, r) => setTimeout(() => r(new Error('timeout')), 15000)),
        ]);
        if (!this.alive) return;
        // Nach dem Auswerten von Three.js dem Browser kurz Luft lassen (Eingaben vor dem Szenenaufbau)
        await new Promise((r) => setTimeout(r, 0));
        const mobile = window.innerWidth < 760;
        // Pixeldichte, Marco 04.11. (Handy unscharf): am Handy bis 2 statt 1,25, am Computer wie bisher bis 1,5.
        // Die 3D-Fläche ist einspaltig nur noch so hoch wie die Bühne mit Rand (onResize), das trägt die Grafikkarte.
        // Als schwach gelten nur noch Geräte mit höchstens 2 Kernen oder 2 GB. Die frühere Grenze 4 traf fast jedes
        // Handy (Chrome meldet 6 GB als 4, Safari meldet auf dem iPhone 4 Kerne): Pixeldichte 1 und keine Wechsel.
        let dpr = Math.min(window.devicePixelRatio || 1, mobile ? 2 : 1.5);
        const schwach = (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2) || (navigator.deviceMemory && navigator.deviceMemory <= 2);
        if (schwach) dpr = Math.min(dpr, 1.25);
        this.dpr = dpr;
        // Untergrenze der adaptiven Qualität (miss): am Handy 1,25, darunter wirkt der Tresor verwaschen; sonst 0,75
        this.dprMin = Math.min(dpr, mobile ? 1.25 : 0.75);
        this.stufenMax = mobile ? 3 : 2;
        this.schwach = !!schwach;
        this.scene = await mod.createVaultScene(this.q('gl'), {
          THREE,
          mobile,
          dpr,
          tex: { logo: this.dataset.texLogo, card: this.dataset.texCard, seed: this.dataset.texSeed, paper: this.dataset.texPaper },
        });
        if (!this.alive) {
          this.scene.dispose();
          this.scene = null;
          return;
        }
        this.scene.setCode(this.code());
        this.onResize();
        // Shader parallel kompilieren (KHR_parallel_shader_compile), damit das erste Bild nicht hängt
        await Promise.race([this.scene.warm(), new Promise((r) => setTimeout(r, 4000))]);
        await new Promise((r) => setTimeout(r, 0));
        if (!this.alive) return;
        // Texturen und erstes Bild vorab in kleinen Schritten (siehe prime in der Szene). Nur eine Beschleunigung:
        // Schlägt sie fehl, läuft der Tresor trotzdem.
        try {
          if (this.scene.prime) await this.scene.prime(() => !this.alive);
        } catch (e) {
          /* erstes Bild dann wie bisher */
        }
        if (!this.alive) return;
        const offen = this.reduced || !this.autoplay || (mobile && this.dataset.mobil === 'offen');
        this.t = offen ? END : 0;
        this.ready = true;
        this.last = performance.now();
        this.classList.remove('is-loading');
        this.classList.add('is-ready');
        this.plan = this.planen();
        this.messung = { n: 0, summe: 0, stufen: 0 };
        this.wecke(800);
      } catch (e) {
        if (this.scene) {
          this.scene.dispose();
          this.scene = null;
        }
        fail(e);
      }
    }

    onResize() {
      const cv = this.q('gl');
      const st = this.q('stage');
      const kopf = this.querySelector('.hvh__h1');
      const scrim = this.q('scrim');
      // Gestapelt, wenn die Bühne unter der Headline beginnt. Nicht an der Textspalte messen: Die hat unter 900 px
      // display: contents und damit keine Box (Rechteck 0), nach dem Scrollen kippte sonst die Abdunklung.
      if (st && kopf) this.stacked = st.getBoundingClientRect().top >= kopf.getBoundingClientRect().bottom - 4;
      // Festgehaltene Höhe der Produktliste (beschrifte) gilt nur für die Breite, bei der sie gemessen wurde
      const nav = this.querySelector('.hvh__nav');
      if (nav && nav.style.minHeight && nav.clientWidth !== this.navBreite) nav.style.minHeight = '';
      if (!cv || !st) return;
      // Layout-Pixel über die offsetParent-Kette, nicht getBoundingClientRect (robust bei Zoom und Transformationen)
      let ox = 0;
      let oy = 0;
      for (let n = st; n && n !== this; n = n.offsetParent) {
        ox += n.offsetLeft;
        oy += n.offsetTop;
      }
      const sr = { width: st.clientWidth, height: st.clientHeight };
      // Einspaltig (Marco 04.11., Handy unscharf): Die 3D-Fläche reicht nur 24 % der Bühnenhöhe über und unter die
      // Bühne statt über den ganzen Hero (Handy 412 px breit: rund 600 statt 1342 px hoch). So bleibt die höhere
      // Pixeldichte flüssig. Ausserhalb lag der Tresor ohnehin zu 86–92 % unter der Abdunklung; die Ränder blenden
      // per CSS-Maske weich aus (.is-gestapelt). Zweispaltig deckt die Fläche wie bisher den ganzen Hero.
      this.classList.toggle('is-gestapelt', !!this.stacked);
      if (this.stacked) {
        const rand = Math.round(0.24 * sr.height);
        const oben = Math.max(0, oy - rand);
        cv.style.top = `${oben}px`;
        cv.style.height = `${oy + sr.height + rand - oben}px`;
      } else if (cv.style.top || cv.style.height) {
        cv.style.top = '';
        cv.style.height = '';
      }
      const cr = { width: cv.clientWidth, height: cv.clientHeight };
      // Szene und Beschriftungen rechnen in Koordinaten der Fläche, die Abdunklung in denen des Heros
      this.stOff = { x: ox - cv.offsetLeft, y: oy - cv.offsetTop };
      this.cvH = cr.height;
      if (scrim) {
        const x = ox;
        const y = oy;
        const h = sr.height;
        // Gestapelt wie im Prototyp dunkel über der Bühne; seit 04.11. einspaltig auch darunter, weil dort jetzt Text und Knöpfe stehen
        scrim.style.background = this.stacked
          ? `linear-gradient(180deg, rgba(5,5,9,0.92) 0px, rgba(5,5,9,0.86) ${Math.max(0, y - 20).toFixed(0)}px, rgba(5,5,9,0.3) ${(y + 0.06 * h).toFixed(0)}px, rgba(5,5,9,0) ${(y + 0.2 * h).toFixed(0)}px, rgba(5,5,9,0) ${(y + 0.8 * h).toFixed(0)}px, rgba(5,5,9,0.3) ${(y + 0.94 * h).toFixed(0)}px, rgba(5,5,9,0.86) ${(y + h + 20).toFixed(0)}px, rgba(5,5,9,0.92) 100%)`
          : `linear-gradient(90deg, rgba(5,5,9,0.9) 0px, rgba(5,5,9,0.72) ${(x * 0.78).toFixed(0)}px, rgba(5,5,9,0.24) ${(x + 0.05 * sr.width).toFixed(0)}px, rgba(5,5,9,0) ${(x + 0.24 * sr.width).toFixed(0)}px)`;
      }
      if (this.scene) {
        this.scene.resize(cr.width, cr.height, { x: this.stOff.x - 0.14 * sr.width, y: this.stOff.y - 0.09 * sr.height, w: 1.28 * sr.width, h: 1.18 * sr.height });
      }
    }

    onPtr(e) {
      if (this.narrow || e.pointerType === 'touch' || this.reduced || this.touch) return;
      this.ptr.x = clamp((e.clientX / window.innerWidth) * 2 - 1, -1, 1);
      this.ptr.y = clamp((e.clientY / window.innerHeight) * 2 - 1, -1, 1);
      if (this.ready && !this.raf) this.wecke(1200);
    }

    replay(neu) {
      this.t = neu || this.t >= END ? 0 : END;
      this.paused = false;
      this.wecke();
    }

    decrypt(t) {
      const instant = this.reduced || this.failed;
      const nodes = this.chars;
      const n = nodes.length;
      if (n) {
        const tick = Math.floor(this.clock * 12);
        for (let i = 0; i < n; i++) {
          const el = nodes[i];
          const rt = ENT0 + (ENT1 - ENT0) * (n > 1 ? i / (n - 1) : 0);
          let txt = null;
          let op;
          let enc = true;
          if (instant || t >= rt) {
            enc = false;
            op = instant ? 1 : 0.5 + 0.5 * eo3(seg(t, rt, rt + 0.22));
          } else if (t >= rt - 0.34) {
            txt = HEX[(tick * 7 + i * 5) % 16];
            op = 0.5;
          } else {
            txt = '–';
            op = 0.22 + 0.1 * seg(t, 1.2, 1.6);
          }
          if (el._enc !== enc) {
            el._enc = enc;
            el.classList.toggle('is-enc', enc);
          }
          if (enc) {
            if (el._z !== txt) {
              el._z = txt;
              el.setAttribute('data-z', txt);
            }
            const os = op.toFixed(2);
            if (el._o !== os) {
              el._o = os;
              el.style.setProperty('--o', os);
              el.style.opacity = '';
            }
          } else {
            const os = op >= 0.999 ? '' : op.toFixed(2);
            if (el._o !== 'd' + os) {
              el._o = 'd' + os;
              el.style.opacity = os;
            }
          }
        }
      }
      const sw = this.q('swiss');
      const rule = this.q('swissRule');
      if (sw) {
        // Schweizer Zeile mit Fahne, sobald der letzte Buchstabe sitzt und die Tür aufschwingt
        const k = instant ? 1 : eo3(seg(t, ENT1 + 0.1, ENT1 + 0.85));
        const glow = instant ? 0 : Math.sin(Math.PI * seg(t, ENT1 + 0.15, ENT1 + 1.3));
        sw.style.opacity = k.toFixed(3);
        sw.style.transform = `translateY(${(6 * (1 - k)).toFixed(2)}px)`;
        sw.style.textShadow = glow > 0.01 ? `0 0 ${(16 * glow).toFixed(1)}px rgba(231,43,223,${(0.38 * glow).toFixed(3)})` : 'none';
        if (rule) rule.style.transform = `scaleX(${(instant ? 1 : eo3(seg(t, ENT1, ENT1 + 0.7))).toFixed(3)})`;
      }
    }

    placeCallouts(t) {
      if (!this.scene) return;
      const show = this.callouts && !this.narrow;
      const off = this.stOff || { x: 0, y: 0 };
      const sh = this.cvH ? this.cvH - off.y : 1e4;
      (this.E.co || []).forEach((el) => {
        const i = Number(el.getAttribute('data-i'));
        const s0 = 8.6 + [0, 0.15, 0.25][i];
        // labelK: Beschriftung eines wechselnden Platzes aus und wieder ein; dazwischen nicht anklickbar
        const lk = this.labelK[i] == null ? 1 : this.labelK[i];
        const o = show ? eo3(seg(t, s0, s0 + 0.6)) * lk : 0;
        const pe = lk < 0.98 ? 'none' : '';
        if (el.style.pointerEvents !== pe) el.style.pointerEvents = pe;
        if (o <= 0.01) {
          if (el.style.visibility !== 'hidden') {
            el.style.visibility = 'hidden';
            el.style.opacity = '0';
          }
          return;
        }
        const p = this.scene.project(i);
        const y = Math.min(p.y - off.y, sh - (el.offsetHeight || 70) - 10);
        el.style.transform = `translate(${(p.x - off.x).toFixed(1)}px, ${y.toFixed(1)}px) translateX(-50%)`;
        el.style.opacity = o.toFixed(3);
        el.style.visibility = 'visible';
      });
    }

    // Goldglanz über «DEINE KEYS.» wie bei den Überschriften der Startseite (vault-gold.js): Band in 1,7 s darüber
    glanz() {
      const el = this.q('glanz');
      if (!el || !el.animate || this.reduced) return;
      el.animate([{ backgroundPosition: '115% 0' }, { backgroundPosition: '-15% 0' }], { duration: 1700, easing: 'cubic-bezier(0.45, 0, 0.2, 1)' });
    }

    glanzPlanen(warte) {
      clearTimeout(this.glanzT);
      if (!this.alive || this.reduced) return;
      this.glanzT = setTimeout(() => {
        if (!this.alive) return;
        if (this.onScreen && !document.hidden) this.glanz();
        this.glanzPlanen(11000 + Math.random() * 4000);
      }, warte);
    }

    // Schleife anstossen; ms: auch bei ruhender Szene so lange weiterzeichnen (Hover, Grösse, Zeiger).
    // Nur hier, beim (Neu-)Start, setzt sich die Zeitmarke zurück; die laufende Schleife plant tick selbst.
    wecke(ms = 0) {
      if (ms) this.wachBis = Math.max(this.wachBis, performance.now() + ms);
      if (!this.alive || this.raf) return;
      if (!this.laufen()) return;
      this.last = performance.now();
      this.raf = requestAnimationFrame(this.tick);
    }

    laufen() {
      if (document.hidden || !this.onScreen || !this.ready || this.failed) return false;
      if (this.paused) return performance.now() < this.wachBis;
      if (this.t < END) return true;
      // Offen: ruhige Szene weiter; bei reduzierter Bewegung nur, solange sich etwas ändert
      if (this.reduced) return performance.now() < this.wachBis;
      return true;
    }

    tick(now) {
      this.raf = 0;
      if (!this.alive) return;
      const dt = Math.min(0.05, Math.max(0, (now - this.last) / 1000));
      this.last = now;
      if (this.ready && !this.paused) {
        this.t += dt * this.tempo;
        if (this.reduced && this.t > END) this.t = END;
      }
      this.clock += dt;
      const p = this.ptr;
      p.sx += (p.x - p.sx) * 0.04;
      p.sy += (p.y - p.sy) * 0.04;
      this.decrypt(this.t);
      if (this.scene && this.ready && this.t >= END) this.showroom(dt);

      if (this.scene) {
        // Nach dem Ende genügen für die ruhige Szene 30 Bilder pro Sekunde (am Handy 24), nach Zeit statt
        // nach Bildern gezählt, damit 120-Hz-Bildschirme nicht doppelt so viel zeichnen. Ein Produktwechsel
        // läuft mit vollem Bildtakt, damit er weich bleibt.
        const settled = this.t > END + 1.2 && !this.paused && this.hot < 0 && !this.wechsel;
        const abstand = settled ? 1000 / (this.narrow ? 24 : 30) : 0;
        this.schritt = (this.schritt || 0) + dt;
        if (!settled || now - (this.zuletzt || 0) >= abstand - 2) {
          const schritt = this.schritt;
          this.schritt = 0;
          this.zuletzt = now;
          this.scene.update(this.t, settled ? Math.min(0.1, schritt) : dt, { x: p.sx, y: p.sy }, this.hot, this.fx);
          this.scene.render();
          this.placeCallouts(this.t);
          if (!settled && this.t > 1.2 && this.t < END) this.miss(dt);
        }
      }

      let ph = 'IDLE';
      for (const x of PH) if (this.t >= x[1]) ph = x[0];
      if (ph !== this.phase) {
        this.phase = ph;
        this.dataset.phase = ph;
      }
      // Erster Glanz, sobald der letzte Buchstabe sitzt; danach plant glanzPlanen weiter
      if (!this.glanzLaeuft && this.t >= ENT1 + 0.45) {
        this.glanzLaeuft = true;
        this.glanz();
        this.glanzPlanen(11000 + Math.random() * 4000);
      }
      // Nächstes Bild direkt planen, ohne die Zeitmarke anzufassen: dt ist so die volle Bilddauer
      // (früher setzte wecke() sie nach der Arbeit neu, die Animation lief dadurch langsamer als eingestellt).
      if (this.alive && this.laufen()) this.raf = requestAnimationFrame(this.tick);
    }

    // ---------- Lebendiger Showroom (Marco 04.11.) ----------

    // Plan für diesen Seitenaufruf (Marco 04.11.): jeder Sockel tauscht einmal, in der Reihenfolge der Zusatzprodukte
    // (Standard Mitte, links, rechts). Nie ein Produkt, das schon steht. Am Handy alle, einer oder keiner (Einstellung);
    // keiner bei reduzierter Bewegung, Datensparmodus oder auf schwachen Handys. start (nur Prüfung) dreht die Reihenfolge.
    planen(start) {
      const n = this.pool.length;
      const sparen = !!(navigator.connection && navigator.connection.saveData);
      let max = n;
      if (this.narrow) max = this.wHandy === 'aus' || this.schwach ? 0 : this.wHandy === 'einmal' ? 1 : n;
      if (!this.wAn || this.reduced || this.failed || sparen || !n) max = 0;
      const plan = [];
      const gezeigt = new Set(this.urls);
      const s0 = Number.isInteger(start) ? ((start % n) + n) % n : 0;
      for (let j = 0; j < n && plan.length < max; j++) {
        const i = (s0 + j) % n;
        if (gezeigt.has(this.pool[i].url)) continue;
        plan.push({ pool: i, id: null });
        gezeigt.add(this.pool[i].url);
      }
      this.wNaechst = this.narrow ? Math.max(14, this.wStart) : this.wStart;
      this.dataset.showroom = plan.length ? 'ruhe' : 'still';
      return plan;
    }

    // Im Endbild, jedes Bild. Zählt nur sichtbare Zeit: Die Schleife läuft nur, solange der Hero zu sehen ist.
    showroom(dt) {
      this.showT += dt;
      this.fx.zeit = this.clock;
      // Schweben beginnt nach 0,8 s und ist nach 3,2 s voll da (Marco 04.11.: vorher zu spät)
      this.fx.idle = this.schweben && !this.reduced ? sine(seg(this.showT, 0.8, 3.2)) : 0;
      // Politur: Goldglanz einmal über jedes der drei Produkte beim Eintreffen
      for (const [platz, ab] of POLITUR) {
        const k = this.reduced ? -1 : (this.showT - ab) / 1.2;
        this.fx.glanz[platz] = k >= 0 && k <= 1 ? k : -1;
      }
      // Goldene Beschriftung nach einem Wechsel nach 1,8 s wieder normal
      for (let i = 0; i < 3; i++) {
        if (this.neuBis[i] && this.showT > this.neuBis[i]) {
          this.neuBis[i] = 0;
          this.goldLabel(i, false);
        }
      }
      if (this.wechsel) {
        this.wechselLauf(dt);
        return;
      }
      if (this.wStopp || this.reduced || this.wFertig >= this.plan.length) return;
      if (!this.wVorbereitet && this.showT > 1) this.vorbereiten();
      const s = this.plan[this.wFertig];
      if (s.fehler) {
        this.plan.splice(this.wFertig, 1);
        return;
      }
      // Pause bei Hover oder Fokus auf einem Produkt, Fokus im Hero, Bühne kaum sichtbar; danach mindestens 6 s Ruhe
      if (this.hot >= 0 || this.fokus || !this.buehneSichtbar || this.paused) {
        this.wNaechst = Math.max(this.wNaechst, this.showT + 6);
        return;
      }
      if (this.showT >= this.wNaechst && s.id != null) this.wechselStart(s);
    }

    // Zusatzprodukte im Leerlauf vorbereiten (Bild laden, prüfen, hochladen), eines nach dem anderen
    async vorbereiten() {
      this.wVorbereitet = true;
      const plan = this.plan;
      for (const s of plan) {
        if (s.id != null || s.fehler) continue;
        await new Promise((r) => ('requestIdleCallback' in window ? requestIdleCallback(() => r(), { timeout: 2500 }) : setTimeout(r, 300)));
        if (!this.alive || !this.scene || plan !== this.plan) return;
        const d = this.pool[s.pool];
        try {
          s.id = await this.scene.prepare({ bild: this.narrow && d.bild_m ? d.bild_m : d.bild }, d.platz);
        } catch (e) {
          s.fehler = true;
          if (this.alive) console.warn('[HODL Vault] Zusatzprodukt nicht gezeigt:', d.titel, e && e.message);
        }
      }
    }

    wechselStart(s) {
      const d = this.pool[s.pool];
      if (!this.scene.swapStart(d.platz, s.id)) {
        s.fehler = true;
        return;
      }
      this.wechsel = { slot: d.platz, d, k: 0, getauscht: false };
      this.dataset.showroom = 'wechsel';
    }

    // Ein Wechsel läuft (rund 2 s). Die Beschriftung blendet mit dem alten Produkt aus, tauscht Name und Link,
    // solange sie unsichtbar ist, und kommt mit dem neuen zurück: Bild, Name und Ziel passen jederzeit zusammen.
    wechselLauf(dt) {
      const w = this.wechsel;
      if (!w.halt) w.k = Math.min(1, w.k + dt / (this.reduced ? 0.05 : WECHSEL_S));
      this.fx.k = w.k;
      this.labelK[w.slot] = w.k < 0.45 ? 1 - eo3(seg(w.k, 0, 0.22)) : eo3(seg(w.k, 0.72, 0.95));
      if (!w.getauscht && w.k >= 0.45) {
        w.getauscht = true;
        this.beschrifte(w.slot, w.d);
      }
      // Die neue Beschriftung kommt golden und findet in 1,6 s zu ihrer Farbe zurück
      if (!w.vergoldet && w.k >= 0.72) {
        w.vergoldet = true;
        this.goldLabel(w.slot, true);
        this.neuBis[w.slot] = this.showT + 1.8;
      }
      this.navLabel(w.slot);
      if (w.k < 1) return;
      this.scene.swapEnd();
      this.labelK[w.slot] = 1;
      this.navLabel(w.slot);
      this.wechsel = null;
      this.fx.k = 0;
      this.wFertig += 1;
      this.wNaechst = this.showT + this.wAbstand;
      this.dataset.showroom = this.wFertig >= this.plan.length ? 'fertig' : 'schweben';
    }

    // Name, Kennzeile und Ziel eines Platzes: Beschriftung in der Bühne, Produktliste am Handy, Klick in der Szene
    beschrifte(i, d) {
      this.urls[i] = d.url;
      const co = (this.E.co || []).find((el) => Number(el.getAttribute('data-i')) === i);
      if (co) {
        co.setAttribute('href', d.url);
        const kk = co.querySelector('.hvh__co-kicker');
        if (kk) kk.textContent = d.kicker || '';
        const tt = co.querySelector('.hvh__co-titel');
        if (tt) {
          if (tt.firstChild && tt.firstChild.nodeType === 3) tt.firstChild.nodeValue = d.titel;
          else tt.insertBefore(document.createTextNode(d.titel), tt.firstChild);
        }
      }
      const a = this.querySelectorAll('.hvh__nav a')[i];
      if (a) {
        // Die Produktliste (Handy) behält ihre Höhe: Kürzere Namen bräuchten eine Zeile weniger, Beschreibung und
        // Knöpfe darunter sprängen sonst mitten im Wechsel 28 px hoch. Längere Namen dürfen sie wachsen lassen.
        const nav = a.parentNode;
        if (nav && nav.offsetHeight && !nav.style.minHeight) {
          nav.style.minHeight = `${nav.offsetHeight}px`;
          this.navBreite = nav.clientWidth;
        }
        a.setAttribute('href', d.url);
        if (a.lastChild && a.lastChild.nodeType === 3) a.lastChild.nodeValue = d.titel;
        else a.appendChild(document.createTextNode(d.titel));
      }
    }

    goldLabel(i, an) {
      const co = (this.E.co || []).find((el) => Number(el.getAttribute('data-i')) === i);
      if (co) co.classList.toggle('is-neu', an);
      const a = this.querySelectorAll('.hvh__nav a')[i];
      if (a) a.classList.toggle('is-neu', an);
    }

    labelVon(i) {
      const co = (this.E.co || []).find((el) => Number(el.getAttribute('data-i')) === i);
      if (!co) return null;
      const tt = co.querySelector('.hvh__co-titel');
      const kk = co.querySelector('.hvh__co-kicker');
      return { url: co.getAttribute('href'), titel: tt && tt.firstChild && tt.firstChild.nodeType === 3 ? tt.firstChild.nodeValue : '', kicker: kk ? kk.textContent : '' };
    }

    navLabel(i) {
      const a = this.querySelectorAll('.hvh__nav a')[i];
      if (!a) return;
      const k = this.labelK[i];
      a.style.opacity = k >= 0.999 ? '' : k.toFixed(3);
      a.style.pointerEvents = k >= 0.98 ? '' : 'none';
    }

    // Adaptive Qualität: Schafft das Gerät die Bilder während der Animation nicht, sinkt die Pixeldichte in Schritten
    // von 0,25 (am Computer höchstens zwei Stufen bis 0,75, am Handy drei bis 1,25).
    miss(dt) {
      const m = this.messung;
      const min = this.dprMin || 0.75;
      if (!m || m.stufen >= (this.stufenMax || 2) || this.dpr <= min) return;
      m.n += 1;
      m.summe += dt;
      if (m.n < 60) return;
      const mittel = m.summe / m.n;
      m.n = 0;
      m.summe = 0;
      if (mittel > 0.028) {
        m.stufen += 1;
        this.dpr = Math.max(min, Math.round((this.dpr - 0.25) * 100) / 100);
        this.scene.setPixelRatio(this.dpr);
      }
    }
  }

  customElements.define('hodl-vault-hero', HodlVaultHero);
})();
